import { useState } from 'react';
import type { ReactNode } from 'react';
import { SelectField } from '@/components/ui/SelectField';
import { Toggle } from '@/components/ui/Toggle';
import { GiftIcon, TagIcon } from '@/components/icons';
import { toast } from '@/store/useToastStore';
import { cn } from '@/lib/cn';
import type { MerchantSettings, RefundMode as RefundModeValue } from '@/types';
import { NumberField, PolicyCard, PolicyRow } from './fields';
import type { SettingsSectionProps } from './fields';

type EnabledKey =
  | 'refundModeOriginal'
  | 'refundModeStoreCredit'
  | 'refundModeGiftCard'
  | 'refundModeDiscountCode';

type BonusKey = 'storeCreditBonusPct' | 'giftCardBonusPct' | 'discountCodeBonusPct';

interface RefundMode {
  key: EnabledKey;
  title: string;
  description: string;
  icon: ReactNode;
  badge?: string;
  bonusKey?: BonusKey;
  bonusLabel?: string;
}

const RupeeIcon = <span className="text-lg font-semibold leading-none">₹</span>;

const REFUND_MODE_VALUE: Record<EnabledKey, RefundModeValue> = {
  refundModeOriginal: 'ORIGINAL_PAYMENT',
  refundModeStoreCredit: 'STORE_CREDIT',
  refundModeGiftCard: 'GIFT_CARD',
  refundModeDiscountCode: 'DISCOUNT_CODE',
};

const REFUND_MODES: RefundMode[] = [
  {
    key: 'refundModeOriginal',
    title: 'Original payment method',
    description: "Refund to the customer's card / UPI via Shopify.",
    icon: RupeeIcon,
  },
  {
    key: 'refundModeStoreCredit',
    title: 'Shopify store credit',
    description: "Customer's native Shopify store credit account.",
    icon: <GiftIcon />,
    badge: 'Most popular',
    bonusKey: 'storeCreditBonusPct',
    bonusLabel: 'Store credit bonus',
  },
  {
    key: 'refundModeGiftCard',
    title: 'Shopify gift card',
    description: 'Single-use, emailed to customer. Plus only.',
    icon: <TagIcon />,
    bonusKey: 'giftCardBonusPct',
    bonusLabel: 'Gift card bonus',
  },
  {
    key: 'refundModeDiscountCode',
    title: 'Discount code',
    description: 'Auto-generated, customer-locked, 90-day expiry.',
    icon: <TagIcon />,
    bonusKey: 'discountCodeBonusPct',
    bonusLabel: 'Discount code bonus',
  },
];

export function RefundModesSection({ settings, onChange }: SettingsSectionProps) {
  const [expanded, setExpanded] = useState<EnabledKey | null>(null);

  const enabledCount = REFUND_MODES.filter((mode) => Boolean(settings[mode.key])).length;

  const activeModeOptions = REFUND_MODES.filter((mode) => Boolean(settings[mode.key])).map((mode) => ({
    value: REFUND_MODE_VALUE[mode.key],
    label: mode.title,
  }));

  return (
    <div className="flex flex-col gap-5">
      <PolicyCard
        title="Refund modes"
        description="Toggle which options customers see at checkout. Per-reason filtering is configured in Return reasons."
      >
        {REFUND_MODES.map((mode) => {
          const enabled = Boolean(settings[mode.key]);
          const canConfigure = enabled && Boolean(mode.bonusKey);
          const isExpanded = expanded === mode.key;
          return (
            <div key={mode.key}>
              <div className="flex items-center gap-4 px-5 py-4 sm:px-6">
                <span
                  className={cn(
                    'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-colors',
                    enabled ? 'bg-brand/10 text-brand' : 'bg-surface-sunken text-ink-subtle',
                  )}
                >
                  {mode.icon}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-ink">{mode.title}</span>
                    {mode.badge ? (
                      <span className="inline-flex items-center rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-brand">
                        {mode.badge}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-ink-muted">{mode.description}</p>
                </div>

                <div className="flex items-center gap-4">
                  {canConfigure ? (
                    <button
                      type="button"
                      onClick={() => setExpanded((current) => (current === mode.key ? null : mode.key))}
                      className="text-sm font-medium text-ink transition-colors hover:text-brand"
                    >
                      Configure
                    </button>
                  ) : null}
                  <Toggle
                    checked={enabled}
                    onChange={(checked) => {
                      if (!checked && enabledCount <= 1) {
                        toast.error("You can't disable all refund modes");
                        return;
                      }
                      onChange({ [mode.key]: checked } as Partial<MerchantSettings>);
                      if (!checked && expanded === mode.key) {
                        setExpanded(null);
                      }
                    }}
                  />
                </div>
              </div>

              {canConfigure && isExpanded && mode.bonusKey ? (
                <div className="border-t border-line bg-surface-sunken/40 px-5 py-4 sm:px-6">
                  <div className="max-w-xs">
                    <NumberField
                      label={mode.bonusLabel ?? 'Bonus'}
                      suffix="%"
                      hint="Extra value added on top of the refund to nudge customers toward this mode."
                      value={settings[mode.bonusKey]}
                      onChange={(value) => onChange({ [mode.bonusKey as BonusKey]: value } as Partial<MerchantSettings>)}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </PolicyCard>

      <PolicyCard title="Exchanges" description="Let customers swap for a different size, colour, or product instead of a refund.">
        <PolicyRow title="Enable exchanges" description="When off, customers can only request refunds — not exchanges.">
          <div className="sm:self-end">
            <Toggle
              checked={Boolean(settings.exchangeEnabled)}
              onChange={(checked) => onChange({ exchangeEnabled: checked })}
            />
          </div>
        </PolicyRow>

        {settings.exchangeEnabled ? (
          <PolicyRow
            title="Exchange refund mode"
            description="Used to settle price differences or refunds arising from an exchange."
          >
            <div className="w-full sm:w-56">
              <SelectField
                value={settings.exchangeRefundMode ?? ''}
                onChange={(event) => onChange({ exchangeRefundMode: event.target.value as RefundModeValue })}
                options={activeModeOptions}
                placeholder={activeModeOptions.length === 0 ? 'No refund modes enabled' : 'Select a mode'}
              />
            </div>
          </PolicyRow>
        ) : null}
      </PolicyCard>
    </div>
  );
}
